import React, { useEffect, useMemo, useRef, useState } from 'react';
import { isEmpty, find, filter, groupBy, some, forEach } from 'lodash';
import { Button, DateField, Divider, MenuItem, SelectField, Stack } from '@curves/ui-base';

import { useChartContext } from "@Local/Dashboard/Components/ChartContext";
import { filterTargetTypeMap, filterAxisTypeMap, filterRangeTypeMap } from "@Local/Dashboard/staticData";
import useChartAdvancedFilterApi from "@Apis/useChartAdvancedFilterApi";
import { useSnackbar } from "notistack";
import { getFirstOfMonth, getLastOfMonth, getSubMonths, isBefore, getDiffDate } from "@Util/moment.util";
const ChartAdvancedTaggedResultBox = React.forwardRef((props, ref) => {
    const { enqueueSnackbar: _snackbar } = useSnackbar();
    const { selectedFilter, setSourceData } = useChartContext();
    const { postChartAdvFilterTaggedApi } = useChartAdvancedFilterApi();
    const {
        dataType: dataTypeProps,
        chartType: chartTypeProps,
        options: optionsProps
    } = props;
    const assignDate = getSubMonths(new Date(), 1);
    const [mainOptions, setMainOptions] = useState([]);
    const [cateOptions, setCateOptions] = useState([]);
    const [hasClubAxis, setHasClubAxis] = useState(false);
    const [hasDateRange, setHasDateRange] = useState(false);
    const [rangeStart] = useState(getFirstOfMonth(assignDate));
    const [rangeEnd] = useState(getLastOfMonth(assignDate));
    const [selectedMainAxis, setSelectedMainAxis] = useState(null);
    const [selectedCateAxis, setSelectedCateAxis] = useState(null);
    const mainSelectRef = useRef(null);
    const cateSelectRef = useRef(null);
    const cateRangeStartRef = useRef(null);
    const cateRangeEndRef = useRef(null);
    const handleChangeMainTarget = (e) => {
        if (!e.target.value) return;
        const { value: tagType } = e.target.value || {}
        if (!tagType) return;
        const {
            memberTagGroupOptions = [],
            orderTagGroupOptions = []
        } = optionsProps || {};
        const tagOption = find(memberTagGroupOptions.concat(orderTagGroupOptions), (option) => option.tagType === tagType);
        const { hasDateRange: isDate = false } = tagOption || {};
        setHasDateRange(isDate);
        setSelectedMainAxis(e.target.value);
        cateSelectRef.current.value = "";
    };
    const handleChangeCateTarget = (e) => {
        if (!e.target.value) return;
        const { value: tagType } = e.target.value || {}
        const {
            memberTagGroupOptions = [],
            orderTagGroupOptions = []
        } = optionsProps || {};
        const tagOption = find(memberTagGroupOptions.concat(orderTagGroupOptions), (option) => option.tagType === tagType);
        const { hasDateRange: isDate = false } = tagOption || {};
        setHasDateRange(hasDateRange || isDate);
        setSelectedCateAxis(e.target.value);
    };
    const handleChangeDateRange = (rangeType) => {
        if (rangeType === filterRangeTypeMap.monthly) {
            cateRangeStartRef.current.resetValue(getFirstOfMonth(assignDate));
            cateRangeEndRef.current.resetValue(getLastOfMonth(assignDate));
        } else if (rangeType === filterRangeTypeMap.quarterly) {
            cateRangeStartRef.current.resetValue(getFirstOfMonth(getSubMonths(assignDate, 2)));
            cateRangeEndRef.current.resetValue(getLastOfMonth(assignDate));
        } else if (rangeType === filterRangeTypeMap.yearly) {
            cateRangeStartRef.current.resetValue(getFirstOfMonth(getSubMonths(assignDate, 12)));
            cateRangeEndRef.current.resetValue(getLastOfMonth(assignDate));
        }
    }
    const handleChartDisplay = async () => {
        const params = {
            dataType: dataTypeProps,
            chartType: chartTypeProps,
            filterItems: selectedFilter,
            mainTarget: mainSelectRef.current.getResult(),
            cateTarget: cateSelectRef.current.getResult(),
            cateStart: null,
            cateEnd: null
        };
        if (isEmpty(params.mainTarget) || isEmpty(params.cateTarget)) {
            _snackbar("請選擇查詢數值", { variant: "info" });
            return;
        }
        if (hasDateRange) {
            params.cateStart = cateRangeStartRef.current.getResult();
            params.cateEnd = cateRangeEndRef.current.getResult();
            if (!isBefore(params.cateStart, params.cateEnd)) {
                _snackbar("查詢結束時間不可小於等於起始時間", { variant: "info" });
                return;
            } else if (getDiffDate(params.cateStart, params.cateEnd, 'year') <= -2) {
                _snackbar("查詢時間不可超過兩年", { variant: "info" });
                return;
            }
        }
        const resp = await postChartAdvFilterTaggedApi(params);
        if (resp) {
            setSourceData(resp.data);
            // console.log(params);
            // console.log(JSON.stringify(resp.data));
        }
        // setSourceData(
        //     [["Main","一年約","二年約","其他"],["台北","258","8259","11"],["新北","294","9298","7"],["雲林","16","238","0"],["嘉義","22","559","1"],["台南","139","3665","0"]]
        // )
    };
    useEffect(() => {
            const {
                clubTagGroupOptions = [],
                memberTagGroupOptions = [],
                orderTagGroupOptions = []
            } = optionsProps || {};
            const selectTags = filter(selectedFilter, (item) => item.targetType !== filterTargetTypeMap.singleClub);
            const tagTypes = Object.keys(groupBy(selectTags, 'tagType')).map(Number);
            const mainOptions = [];
            const cateOptions = [];
            let isClubAxis = some(selectedFilter, { targetType: filterTargetTypeMap.singleClub });
            forEach(clubTagGroupOptions, item => {
                const { tagType, tagTypeText, filterAxisType } = item;
                if (tagTypes.indexOf(item.tagType) >= 0) {
                    mainOptions.push({ text: tagTypeText, value: tagType, axisType: filterAxisType });
                    cateOptions.push({ text: tagTypeText, value: tagType, axisType: filterAxisType });
                    isClubAxis = true;
                }
            });
            forEach(memberTagGroupOptions, item => {
                const { tagType, tagTypeText, filterAxisType } = item;
                if (tagTypes.indexOf(item.tagType) >= 0) {
                    mainOptions.push({ text: tagTypeText, value: tagType, axisType: filterAxisType });
                    cateOptions.push({ text: tagTypeText, value: tagType, axisType: filterAxisType });
                }
            });
            forEach(orderTagGroupOptions, item => {
                const { tagType, tagTypeText, filterAxisType } = item;
                if (tagTypes.indexOf(item.tagType) >= 0) {
                    mainOptions.push({ text: tagTypeText, value: tagType, axisType: filterAxisType });
                    cateOptions.push({ text: tagTypeText, value: tagType, axisType: filterAxisType });
                }
            });
            setMainOptions(mainOptions);
            setCateOptions(cateOptions);
            setHasClubAxis(isClubAxis);
            if (mainSelectRef) mainSelectRef.current.value = "";
            if (cateSelectRef) cateSelectRef.current.value = "";
            setHasDateRange(false);
        },
        // eslint-disable-next-line
        [selectedFilter, optionsProps]
    );
    const MainSelectField = useMemo(
        () =>
            <SelectField ref={mainSelectRef} label={'主數值'} defaultValue={selectedMainAxis} onChange={handleChangeMainTarget} fullWidth>
                <MenuItem value="" disabled>
                    <em>請選擇</em>
                </MenuItem>
                {mainOptions.length > 0 && mainOptions.map((option, index) => (
                    <MenuItem key={index}
                              value={{ axisType: option.axisType, value: option.value }}>
                        <em>{option.text}</em>
                    </MenuItem>
                ))}
            </SelectField>
        ,
        // eslint-disable-next-line
        [mainOptions]
    );
    const CateSelectField = useMemo(
        () =>
            <SelectField ref={cateSelectRef} label={'分類數值'} defaultValue={selectedCateAxis} onChange={handleChangeCateTarget} fullWidth>
                <MenuItem value="" disabled>
                    <em>請選擇</em>
                </MenuItem>
                <MenuItem value={{ axisType: filterAxisTypeMap.none }}>
                    <em>不分類</em>
                </MenuItem>
                {hasClubAxis && (
                    <MenuItem value={{ axisType: filterAxisTypeMap.club }}>
                        <em>店舖</em>
                    </MenuItem>
                )}
                {hasDateRange && (
                    <MenuItem value={{ axisType: filterAxisTypeMap.date }}>
                        <em>時間(月)</em>
                    </MenuItem>
                )}
                {cateOptions.length > 0 && cateOptions.map((option, index) => {
                    const { value: mainValue } = selectedMainAxis || {};
                    return (
                        <MenuItem key={index}
                                  value={{ axisType: option.axisType, value: option.value }}
                                  disabled={option.value === mainValue}
                        >
                            <em>{option.text}</em>
                        </MenuItem>
                    );
                })}
            </SelectField>
        ,
        // eslint-disable-next-line
        [hasClubAxis, hasDateRange, cateOptions, selectedMainAxis]
    )
    const CateDateRangeField = useMemo(
        () =>
            hasDateRange && (
                <React.Fragment>
                    <label className="formlabel"><span>時間限制</span></label>
                    <Stack direction="row" spacing={1}>
                        <DateField ref={cateRangeStartRef} placeholder="起始時間" defaultValue={rangeStart} />
                        <DateField ref={cateRangeEndRef} placeholder="結束時間" defaultValue={rangeEnd} />
                    </Stack>
                    <Stack direction="row" spacing={1}>
                        <Button variant="outlined" size="small" onClick={() => handleChangeDateRange(filterRangeTypeMap.monthly)} color="primary">月</Button>
                        <Button variant="outlined" size="small" onClick={() => handleChangeDateRange(filterRangeTypeMap.quarterly)} color="primary">季</Button>
                        <Button variant="outlined" size="small" onClick={() => handleChangeDateRange(filterRangeTypeMap.yearly)} color="primary">年</Button>
                    </Stack>
                </React.Fragment>
            )
        ,
        // eslint-disable-next-line
        [hasDateRange]
    )
    return <React.Fragment>
        <Stack direction="column" spacing={1}>
            {MainSelectField}
            {CateSelectField}
            {CateDateRangeField}
        </Stack>
        <Divider className="my-2" />
        <Stack direction="row" spacing={1}>
            <Button variant="contained" color="primary" onClick={handleChartDisplay}>產生圖表</Button>
        </Stack>
    </React.Fragment>
});
export default ChartAdvancedTaggedResultBox;